import { KeyboardEvent, MouseEvent, useEffect, useRef } from 'react';
import { ArrowDownward, ArrowUpward, SwapVert } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { ENTER_KEY } from 'common/constants/general';

type SortDirection = 'asc' | 'desc';

interface WorkboardSortHeaderProps {
  title: string;
  direction: SortDirection | null;
  onClick: (event: { shiftKey: boolean }) => void;
}

// Spreadsheet rebuilds on sort; remember which header to refocus after remount.
let pendingFocusTitle: string | null = null;

export const markWorkboardSortHeaderForFocus = (title: string) => {
  pendingFocusTitle = title;
};

const getSortLabel = (title: string, direction: SortDirection | null) => {
  if (direction === 'asc') {
    return `Sort by ${title}, currently ascending`;
  }
  if (direction === 'desc') {
    return `Sort by ${title}, currently descending`;
  }
  return `Sort by ${title}`;
};

const Root = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  font: 'inherit',
  gap: 2,
  margin: 0,
  padding: 0,
  userSelect: 'none',
  outline: 'none',
  '&:hover .sort-hint': {
    opacity: 0.6,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const WorkboardSortHeader = ({ title, direction, onClick }: WorkboardSortHeaderProps) => {
  const theme = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isActive = direction !== null;

  // Mount-only: direction updates on the *old* instance would clear pendingFocusTitle
  // before the remounted header can claim focus.
  useEffect(() => {
    if (pendingFocusTitle !== title) {
      return undefined;
    }
    pendingFocusTitle = null;
    const frame = requestAnimationFrame(() => {
      buttonRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount after sort
  }, []);

  const handleActivate = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    markWorkboardSortHeaderForFocus(title);
    onClick({ shiftKey: event.shiftKey });
  };

  const handleMouseDown = (event: MouseEvent) => {
    // Keep datasheet from selecting the cell / stealing focus before click.
    event.stopPropagation();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === ENTER_KEY || event.key === ' ') {
      handleActivate(event);
    }
  };

  const activeIconStyle = { fill: theme.palette.primary.main, fontSize: '18px' } as const;
  const hintIconSx = { fontSize: 14, opacity: 0.25, transition: 'opacity 0.15s' } as const;

  return (
    <Root
      ref={buttonRef}
      type="button"
      data-workboard-sort-header="true"
      data-workboard-sort-title={title}
      aria-label={getSortLabel(title, direction)}
      onMouseDown={handleMouseDown}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}>
      <span style={{ fontWeight: isActive ? 'bold' : 'inherit' }}>{title}</span>
      {direction === 'asc' && <ArrowUpward style={activeIconStyle} />}
      {direction === 'desc' && <ArrowDownward style={activeIconStyle} />}
      {!isActive && <SwapVert className="sort-hint" sx={hintIconSx} />}
    </Root>
  );
};

export default WorkboardSortHeader;
